import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IApiService } from '../../iservices/iApi';
import { IApiBaseObject } from '../../iModels/api-base-object.model';
import { ModelBindingService } from '../binding.model.service';
import { API_ENDPOINTS } from '../api-endpoints-constants';
import { SharedService } from '../shared.service';
import { SmsEmailApiService } from '../propertyMngServices/sms-email-api.service';
import { IMessagingService } from '../../iservices/iCommon/iMessaging';
import {
  IAwsEmailTemplateListResponse,
  IAwsEmailTemplateDetails,
  IAwsEmailTemplateCreateRequest,
  IAwsEmailTemplateUpdateRequest,
  ISendAwsEmailRequest,
  IPinBotTemplateListParams,
  ISystemTemplate,
  IPinBotTemplateCreateRequest,
  IPinBotTemplateUpdateRequest,
  IPinBotMessageRequest,
  IWhatsAppRequest,
  IMessagingProviderCredentials
} from '../../iModels/iCommonModels/messaging.model';

/**
 * @desc calls the separate "messaging" microservice (AwsMails / Mails / PinBot / WhatsApp)
 * described in the swagger contract, using baseUrlOverride to hit environment.messagingApiUrl.
 */
@Injectable({
  providedIn: 'root'
})
export class MessagingService implements IMessagingService {
  private apiObject: IApiBaseObject;
  private readonly baseUrl = environment.messagingApiUrl;

  constructor(
    @Inject('IApiService') private apiService: IApiService,
    private modelBindingService: ModelBindingService,
    private sharedService: SharedService,
    private smsEmailApiService: SmsEmailApiService
  ) {}

  // ---------- Email : AWS SES templates ----------
  getAwsEmailTemplates(pageSize?: number, nextToken?: string): Observable<IAwsEmailTemplateListResponse> {
    let url = API_ENDPOINTS.AWS_MAILS_TEMPLATES;
    const query: string[] = [];
    if (pageSize) { query.push(`PageSize=${pageSize}`); }
    if (nextToken) { query.push(`NextToken=${encodeURIComponent(nextToken)}`); }
    if (query.length) { url += '?' + query.join('&'); }
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false, this.baseUrl);
    return this.apiService.getApi(this.apiObject);
  }

  getAwsEmailTemplateDetails(templateName: string): Observable<IAwsEmailTemplateDetails> {
    const url = `${API_ENDPOINTS.AWS_MAILS_TEMPLATES}/${encodeURIComponent(templateName)}`;
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false, this.baseUrl);
    return this.apiService.getApi(this.apiObject);
  }

  createAwsEmailTemplate(payload: IAwsEmailTemplateCreateRequest): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.AWS_MAILS_TEMPLATES, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  updateAwsEmailTemplate(templateName: string, payload: IAwsEmailTemplateUpdateRequest): Observable<any> {
    const url = `${API_ENDPOINTS.AWS_MAILS_TEMPLATES}/${encodeURIComponent(templateName)}`;
    this.apiObject = this.modelBindingService.setApiCallObject(url, payload, true, false, this.baseUrl);
    return this.apiService.putApi(this.apiObject);
  }

  deleteAwsEmailTemplate(templateName: string): Observable<any> {
    const url = `${API_ENDPOINTS.AWS_MAILS_TEMPLATES}/${encodeURIComponent(templateName)}`;
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false, this.baseUrl);
    return this.apiService.deleteApi(this.apiObject);
  }

  sendAwsEmail(payload: ISendAwsEmailRequest): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.AWS_MAILS_SEND_WITH_TEMPLATE, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  // ---------- Email : Gmail (no templates) ----------
  sendSimpleEmail(formData: FormData): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.MAILS_SEND_SIMPLE, formData, true, true, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  sendDynamicEmail(payload: any): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.MAILS_SEND_DYNAMIC, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  // ---------- WhatsApp : PinBot templates (template listing/preview target) ----------
  getPinBotTemplates(params: IPinBotTemplateListParams): Observable<any> {
    const query: string[] = [];
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        query.push(`${key}=${encodeURIComponent(params[key])}`);
      }
    });
    const url = API_ENDPOINTS.PINBOT_TEMPLATES + (query.length ? '?' + query.join('&') : '');
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false, this.baseUrl);
    return this.apiService.getApi(this.apiObject);
  }

  // served by the main API (environment.url) so the X-San-Jwt session header is used
  getSystemTemplates(wabaId: string): Observable<ISystemTemplate[]> {
    const url = `${API_ENDPOINTS.CAMPAIGNS_TEMPLATES}?wabaId=${encodeURIComponent(wabaId)}`;
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false);
    return this.apiService.getApi(this.apiObject);
  }

  getPinBotTemplateById(templateId: string, apiKey: string): Observable<any> {
    const url = `PinBot/templates/${encodeURIComponent(templateId)}?ApiKey=${encodeURIComponent(apiKey)}`;
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false, this.baseUrl);
    return this.apiService.getApi(this.apiObject);
  }

  createPinBotTemplate(payload: IPinBotTemplateCreateRequest): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.PINBOT_TEMPLATES, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  updatePinBotTemplate(payload: IPinBotTemplateUpdateRequest): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.PINBOT_TEMPLATE_UPDATE, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  deletePinBotTemplate(name: string, messageTemplateId: string, wabaId: string, apiKey: string): Observable<any> {
    const query = `Name=${encodeURIComponent(name)}&MessageTemplateId=${encodeURIComponent(messageTemplateId)}` +
      `&WabaId=${encodeURIComponent(wabaId)}&ApiKey=${encodeURIComponent(apiKey)}`;
    const url = `${API_ENDPOINTS.PINBOT_TEMPLATES}?${query}`;
    this.apiObject = this.modelBindingService.setApiCallObject(url, {}, true, false, this.baseUrl);
    return this.apiService.deleteApi(this.apiObject);
  }

  sendPinBotMessage(payload: IPinBotMessageRequest): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.PINBOT_MESSAGES, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  // ---------- WhatsApp : generic gateway (send only, no templates) ----------
  sendWhatsApp(payload: IWhatsAppRequest): Observable<any> {
    this.apiObject = this.modelBindingService.setApiCallObject(
      API_ENDPOINTS.WHATSAPP_SEND, payload, true, false, this.baseUrl);
    return this.apiService.postApi(this.apiObject);
  }

  // ---------- credentials resolution from existing per-property sms-email-api / api-parameter config ----------
  resolveProviderCredentials(apiFor: 'Email' | 'WhatsApp'): Observable<IMessagingProviderCredentials> {
    const propertyId = this.sharedService.getDataFromStorage('propertyId');
    return this.smsEmailApiService.getSmsEmailApiDetails({ page: 1, size: 0 }).pipe(
      switchMap((res: any) => {
        const rows: any[] = (res && res.data) || [];
        // tslint:disable-next-line:triple-equals
        const match = rows.find((r) => r.apiFor === apiFor && r.propertyId == propertyId);
        if (!match) { return of({} as IMessagingProviderCredentials); }
        return this.smsEmailApiService.getApiParamDetails({ propertyAPIId: match.id, page: 1, size: 0 }).pipe(
          map((paramRes: any) => {
            const paramRows: any[] = (paramRes && paramRes.data) || [];
            const credentials: IMessagingProviderCredentials = {};
            paramRows.forEach((p) => {
              switch (p.parameter) {
                case 'ApiKey': credentials.ApiKey = p.parameterValue; break;
                case 'ApiSecret': credentials.ApiSecret = p.parameterValue; break;
                case 'ApiUrl': credentials.ApiUrl = p.parameterValue; break;
                case 'WabaId': credentials.WabaId = p.parameterValue; break;
                case 'PhoneNumberId': credentials.PhoneNumberId = p.parameterValue; break;
                case 'SenderMobileNo': credentials.SenderMobileNo = p.parameterValue; break;
                default: break;
              }
            });
            return credentials;
          })
        );
      })
    );
  }
}
